import produce from 'immer';
import reactUpdate from 'immutability-helper';
import {v4 as uuidv4} from 'uuid';

import {assert, assertAndContinue} from 'Asserts';
import {DEFAULT_CONCENTRATION_UNITS, DEFAULT_VOLUME_UNITS} from 'constants/MeasurementConstants';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {DEFAULT_DEFAULT_VALUE} from 'notebook/data-tables/DataTableConstants';
import {ClientId} from 'notebook/ELNConstants';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  BaseTagValueStructuredTableColumn,
  ConcentrationStructuredTableColumn,
  DEFAULT_COLUMN_WIDTH,
  QuantityStructuredTableColumn,
  StructuredTableColumn,
  TagValueStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {PROCESS_WIDER_COLUMN_WIDTH} from 'processes/ProcessConstants';
import {fieldToColumnWidth} from 'runs/Columns';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {isSnapshotField, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {
  isFirstLookupStepWithSchemaField,
  isValidFirstSnapshotFieldStep,
} from 'schemas/SnapshotFieldLookupConfigUtil';
import uniqueId from 'util/uniqueId';

// This functionality is mirrored in benchling/notebook/structured_tables/models/structured_table_column.py.
// Make sure any changes here are reflected there.
function getDefaultColumnWidth(columnType: TableColumnType): number {
  switch (columnType) {
    case TableColumnType.CONCENTRATION:
    case TableColumnType.CONTAINERS:
    case TableColumnType.NUMBER_OF_CONTAINERS:
    case TableColumnType.PARENT_STORABLE:
    case TableColumnType.PARTIES:
    case TableColumnType.SAMPLE_RESTRICTION_STATUS:
      return DEFAULT_COLUMN_WIDTH + 50;
    case TableColumnType.PROCESS_TASK:
    case TableColumnType.PROCESS_TASK_ASSIGNEE:
    case TableColumnType.PROCESS_TASK_STATUS:
    case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
      return PROCESS_WIDER_COLUMN_WIDTH;
    default:
      return DEFAULT_COLUMN_WIDTH;
  }
}

function getDefaultUnits(columnType: TableColumnType): string {
  switch (columnType) {
    case TableColumnType.CONCENTRATION:
      return DEFAULT_CONCENTRATION_UNITS;
    case TableColumnType.QUANTITY:
      return DEFAULT_VOLUME_UNITS;
    default:
      return DEFAULT_VOLUME_UNITS;
  }
}

function getSelectorId(schemaField: SchemaField): string | null {
  return schemaField.schemaFieldSelectorId || schemaField.legalTextSelectorId || null;
}

export function getSelectorIdsFromFields(schemaFields: Array<SchemaField>): Array<string> {
  return schemaFields
    .map((schemaField) => getSelectorId(schemaField))
    .filter((selectorId) => selectorId != null);
}

function getSnapshotFieldInputSchemaFieldApiId(
  schemaField: SchemaField,
  allSchemaFields?: Array<SchemaField>
): string | null {
  if (isSnapshotField(schemaField)) {
    assert(allSchemaFields != null, 'Expected allSchemaFields to be passed');
    const firstLookupStep = schemaField.lookupConfig.lookupSteps[0];
    assert(
      isValidFirstSnapshotFieldStep(firstLookupStep),
      `Unexpected first snapshot field step ${firstLookupStep.type}`
    );
    if (!isFirstLookupStepWithSchemaField(firstLookupStep)) {
      return null;
    }
    return firstLookupStep.schemaField;
  }
  return null;
}

function getSnapshotFieldGridPositionInputSchemaFieldApiId(schemaField: SchemaField): string | null {
  if (isSnapshotField(schemaField)) {
    const firstLookupStep = schemaField.lookupConfig.lookupSteps[0];
    assert(
      isValidFirstSnapshotFieldStep(firstLookupStep),
      `Unexpected first snapshot field step ${firstLookupStep.type}`
    );
    if (firstLookupStep.type === LookupStepType.CONTAINER_BY_GRID_POSITION) {
      return firstLookupStep.gridPositionSchemaField;
    }
  }
  return null;
}

function getSnapshotFieldInputIsProcessTask(schemaField: SchemaField): boolean {
  if (isSnapshotField(schemaField)) {
    const firstLookupStep = schemaField.lookupConfig.lookupSteps[0];
    assert(
      isValidFirstSnapshotFieldStep(firstLookupStep),
      `Unexpected first snapshot field step ${firstLookupStep.type}`
    );
    return firstLookupStep.type === LookupStepType.PROCESS_TASK;
  }
  return false;
}

export function makeSchemaFieldStructuredColumn(
  schemaField: SchemaField,
  selectorsById: {[selectorId: string]: SchemaFieldSelector},
  columnGroup?: ColumnGroup,
  // Needed for code paths that support snapshot field columns
  allSchemaFields?: Array<SchemaField>
): BaseTagValueStructuredTableColumn<ClientId> {
  const selectorId = getSelectorId(schemaField);
  const selector = selectorId == null ? null : selectorsById[selectorId];
  // We assert here because, while we need selectors for selector fields, we often skip
  // resolving the selector and don't actually block the user.
  // If you run into this error in development, you should most likely be passing selectorsById
  // to your table. (see `useSelectorsById.ts`)
  assertAndContinue(
    selectorId == null || selector != null,
    'Selector is expected to be loaded',
    {
      selectorId,
    },
    {fingerprint: ['benchling', 'data-input', 'selector-expected-to-be-loaded']}
  );

  const {frontEndMetadata, ...structuredTableColumn} = makeStructuredTableColumn({
    columnType: TableColumnType.TAG_VALUE,
    name: schemaField.displayName,
    columnGroup,
  });
  return {
    ...structuredTableColumn,
    columnType: TableColumnType.TAG_VALUE,
    schemaFieldType: schemaField.fieldType,
    schemaFieldDisplayName: schemaField.displayName,
    schemaFieldDescription: schemaField.tooltipText,
    schemaFieldDecimalPrecision: schemaField.decimalPrecision,
    schemaFieldIsComputed: schemaField.isComputed,
    schemaFieldIsConvertedFromLink: schemaField.isConvertedFromLink,
    schemaFieldIsSnapshot: schemaField.isSnapshot,
    snapshotFieldInputSchemaFieldApiId: getSnapshotFieldInputSchemaFieldApiId(schemaField, allSchemaFields),
    snapshotFieldGridPositionInputSchemaFieldApiId:
      getSnapshotFieldGridPositionInputSchemaFieldApiId(schemaField),
    snapshotFieldInputIsProcessTask: getSnapshotFieldInputIsProcessTask(schemaField),
    schemaFieldApiId: schemaField.apiId,
    tagSchemaFieldId: schemaField.id,
    selector,
    width: fieldToColumnWidth(schemaField),
    isReadOnly: false,
    isRequired: schemaField.isRequired,
    isArchived: isSchemaFieldArchived(schemaField),
    unitApiIdentifier: schemaField.unitApiIdentifier,
  };
}

export function makeStructuredTableColumn({
  columnType,
  name,
  columnGroup,
  isRequired = false,
  isReadOnly = false,
  isProtected = false,
  defaultValue = DEFAULT_DEFAULT_VALUE,
  width,
}: {
  columnType: TableColumnType;
  name: string;
  columnGroup?: ColumnGroup | null;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isProtected?: boolean;
  defaultValue?: Cell;
  width?: number;
}): StructuredTableColumn {
  const isUnitsColumn =
    columnType === TableColumnType.CONCENTRATION || columnType === TableColumnType.QUANTITY;

  const base: StructuredTableColumn = {
    // structuredTableColumn used to be dataTableColumn and use DataTableColumn on the backend, so
    // use dtcol as the cid prefix for now.
    cid: uniqueId('dtcol'),
    provenanceUuid: uuidv4(),
    name,
    columnType,
    // The server always serializes columnGroup (as null when unset). Query resolution keys embed the
    // column definition, so a missing key would not match the same column after a reload.
    columnGroup: columnGroup ?? null,
    width: width ?? getDefaultColumnWidth(columnType),
    defaultValue,
    isReadOnly,
    isRequired,
    isProtected,
    isArchived: false,
    isReported: true,
  };
  if (isUnitsColumn) {
    (base as QuantityStructuredTableColumn | ConcentrationStructuredTableColumn).units =
      getDefaultUnits(columnType);
  }
  return base;
}

export function getSchemaFieldColumnIds(structuredTableColumns: Array<StructuredTableColumn>): Array<number> {
  const schemaFieldColumnIds: Array<number> = [];
  for (const column of structuredTableColumns) {
    if (column.columnType === TableColumnType.TAG_VALUE) {
      const fieldId = (column as TagValueStructuredTableColumn).tagSchemaFieldId;
      if (fieldId != null) {
        schemaFieldColumnIds.push(fieldId);
      }
    }
  }
  return schemaFieldColumnIds;
}

export function withStructuredTableColumnNameSet(table: Table, i: number, label: string): Table {
  const columnConstraint = table.columnConstraints[i];
  assert(
    columnConstraint?.structuredTableColumn != null,
    `Tried to rename structured table column but columnConstraint or structuredTableColumn did not exist for table. Provenance uuid: ${table.provenanceUuid}`
  );
  // The structured table adapter decides which columns are renamable (see
  // GenericStructuredTableAdapter.isColumnRenamable) and records that on the column constraint, so
  // the context menu and this mutation stay in agreement.
  assert(
    !columnConstraint.editLabelDisabled,
    `Tried to rename a structured table column that is not renamable. Column type: ${columnConstraint.structuredTableColumn.columnType}`
  );
  const newColumnConstraint: ColumnConstraint = produce(columnConstraint, (draftColumnConstraint) => {
    draftColumnConstraint.structuredTableColumn!.name = label;
  });
  return reactUpdate(table, {columnConstraints: {$splice: [[i, 1, newColumnConstraint]]}});
}
